import React from 'react';
import './styles/OrderProgressVerticalM.css';

interface OrderProgressVerticalMProps {
  trackingHistory: Array<{ status: string; timestamp: string; location?: string; notes?: string }>;
  orderStatus?: string;
  createdAt?: string;
  estimatedDelivery?: string;
  deliveryType?: 'immediate' | 'scheduled';
  scheduledDeliveryDate?: string;
  scheduledDeliveryTime?: string;
  paymentMode?: string;
}

const OrderProgressVerticalM: React.FC<OrderProgressVerticalMProps> = ({
  trackingHistory,
  orderStatus,
  createdAt,
  estimatedDelivery,
  deliveryType,
  scheduledDeliveryDate,
  scheduledDeliveryTime,
  paymentMode,
}) => {
  const getPaymentStatus = () => {
    if (paymentMode === 'razorpay' || paymentMode === 'Razorpay') {
      return 'Prepaid';
    }
    return paymentMode || '';
  };

  // Define the order status flow
  const statusFlow = [
    { key: 'pending', label: 'Order Placed' },
    { key: 'confirmed', label: 'Confirmed' },
    { key: 'preparing', label: 'Packed at Warehouse' },
    { key: 'out_for_delivery', label: 'Out for Delivery' },
    { key: 'delivered', label: 'Delivered' }
  ];

  // Get current step index based on order status
  const getCurrentStepIndex = () => {
    const index = statusFlow.findIndex(step => step.key === orderStatus);
    return index >= 0 ? index : 0;
  };

  const currentStepIndex = getCurrentStepIndex();

  // Format timestamp to show time only
  const formatTime = (isoString: string) => {
    try {
      const date = new Date(isoString);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  // Get timestamp for each status from tracking history
  const getStatusTime = (statusKey: string, index: number) => {
    // For first step (Order Placed/pending), always use createdAt
    if (index === 0 && createdAt) {
      return formatTime(createdAt);
    }

    // Try to find exact match in tracking history
    const entry = trackingHistory.find(h => h.status === statusKey);
    if (entry && entry.timestamp) {
      return formatTime(entry.timestamp);
    }

    // For completed steps without exact tracking entry
    if (index < currentStepIndex) {
      const laterEntries = trackingHistory.filter(h => {
        const entryIndex = statusFlow.findIndex(s => s.key === h.status);
        return entryIndex > index;
      });

      if (laterEntries.length > 0) {
        const sortedEntries = laterEntries.sort((a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        return formatTime(sortedEntries[0].timestamp);
      }

      if (createdAt) {
        return formatTime(createdAt);
      }
    }

    return '';
  };

  // Determine step status: completed, active, or pending
  const getStepStatus = (index: number) => {
    if (index < currentStepIndex) return 'completed';
    if (index === currentStepIndex) return 'active';
    return 'pending';
  };

  // Get ETA text
  const getETAText = () => {
    // For scheduled deliveries, show the booked window
    if (
      deliveryType === 'scheduled' &&
      scheduledDeliveryDate &&
      scheduledDeliveryTime
    ) {
      const [start, end] = scheduledDeliveryTime.split('-');
      const datePart = new Date(scheduledDeliveryDate);
      const formatTime = (time: string) => {
        const [h, m] = time.split(':').map(Number);
        const d = new Date(datePart);
        d.setHours(h || 0, m || 0, 0, 0);
        return d.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        });
      };
      const startLabel = formatTime(start);
      const endLabel = formatTime(end);
      return `Scheduled for ${startLabel} - ${endLabel}`;
    }

    if (estimatedDelivery) {
      const eta = new Date(estimatedDelivery);
      const now = new Date();
      const diffMs = eta.getTime() - now.getTime();
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      if (diffMinutes < 60) {
        return `Arriving in ${diffMinutes} minutes`;
      } else {
        const hours = Math.floor(diffMinutes / 60);
        return `Arriving in ${hours > 1 ? hours + ' hours' : '1 hour'}`;
      }
    }
    return 'Arriving in 18 minutes';
  };

  const [collapsed, setCollapsed] = React.useState(true);

  return (
    <div className="order-progress-vertical-m">
      {/* ETA Header */}
      <div className="eta-header">
        <div className="eta-badge">
          <span className="eta-label">ETA</span>
        </div>
        <div className="eta-content">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            <h3 className="eta-time">{getETAText()}</h3>
            <button
              type="button"
              className="eta-collapse-toggle"
              onClick={() => setCollapsed(prev => !prev)}
              aria-label={collapsed ? 'Expand timeline' : 'Collapse timeline'}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                color: '#000',
                fontSize: '40px',
                lineHeight: 1
              }}
            >
              {collapsed ? '▾' : '▴'}
            </button>
          </div>
          <p className="eta-details">
            Placed at {createdAt ? formatTime(createdAt) : '4:12'} PM · <b>{getPaymentStatus()}</b>
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      {!collapsed && (
        <div className="progress-steps-m">
          {statusFlow.map((step, index) => {
            const stepStatus = getStepStatus(index);
            const timeStamp = getStatusTime(step.key, index);

            return (
              <div key={step.key} className="progress-step-container-m">
                <div className={`progress-step-m ${stepStatus}`}>
                  {/* Status Icon */}
                  <div className={`step-icon-m ${stepStatus}`}>
                    {(stepStatus === 'completed' || stepStatus === 'active') && (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M13.3334 4L6.00008 11.3333L2.66675 8"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>

                  {/* Connector Line */}
                  {index < statusFlow.length - 1 && (
                    <div className={`step-connector-m ${stepStatus}`}></div>
                  )}

                  {/* Status Text */}
                  <div className="step-content-m">
                    <p className="step-label-m">{step.label}</p>
                  </div>

                  {/* Timestamp */}
                  <p className="step-time-m">{timeStamp}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Active Status Badge */}
      {orderStatus === 'out_for_delivery' && (
        <div className="active-status-badge">
          <span className="status-badge-text">Out for Delivery</span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="delivery-icon">
            <path d="M13.5 3H3.5C2.94772 3 2.5 3.44772 2.5 4V12C2.5 12.5523 2.94772 13 3.5 13H13.5M13.5 3V13M13.5 3H15.5L17.5 6M13.5 13H15.5M17.5 6V13M17.5 6H13.5V13M17.5 13H15.5M15.5 13C15.5 14.1046 14.6046 15 13.5 15C12.3954 15 11.5 14.1046 11.5 13M15.5 13C15.5 14.1046 16.3954 15 17.5 15C18.6046 15 19.5 14.1046 19.5 13M6.5 13C6.5 14.1046 5.60457 15 4.5 15C3.39543 15 2.5 14.1046 2.5 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="action-icon">
            <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10 7V10H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default OrderProgressVerticalM;